import nacl from 'tweetnacl'
import { decodeBase64, decodeUTF8, encodeBase64, encodeUTF8 } from 'tweetnacl-util'
import { getIdentity, getOrCreateIdentity } from './keyStore'

/**
 * Ключ секретного звонка и его конверт (docs/encryption/E2E-CALLS.md, §3).
 *
 * Звонящий создаёт случайный ключ звонка (32 байта) и кладёт его в конверт
 * crypto_box, зашифрованный ключом устройства собеседника. Общий секрет пары
 * симметричен, поэтому конверт открывают обе стороны — ключ звонка нигде не
 * хранится, после перезагрузки он заново достаётся из конверта.
 *
 * В LiveKit ключ подаётся СТРОКОЙ (base64-текст): из строки SDK выводит ключ
 * через PBKDF2, как и нативные SDK; из байтов — через HKDF, чего iOS не умеет.
 */

export interface CallKeyEnvelope {
  v: 1
  alg: 'box'
  spk: string   // ключ устройства отправителя (звонящего), base64
  rpk: string   // ключ устройства получателя, которым зашифрован конверт, base64
  n: string     // nonce 24 байта, base64
  c: string     // шифротекст, base64
}

export interface CallKeyBinding {
  chatId: number
  callerId: number
  calleeId: number
}

export type OpenCallKeyResult =
  | { ok: true; key: string; peerPublicKey: string }
  | { ok: false; reason: 'no_identity' | 'other_device' | 'bad_envelope' | 'mismatch' }

/** Новый ключ звонка: base64 от 32 случайных байт (44 символа). */
export function generateCallKey(): string {
  return encodeBase64(nacl.randomBytes(32))
}

export function isCallKeyEnvelope(raw: unknown): raw is CallKeyEnvelope {
  const o = raw as CallKeyEnvelope
  return !!o && typeof o === 'object' && o.v === 1 && o.alg === 'box'
    && typeof o.spk === 'string' && typeof o.rpk === 'string'
    && typeof o.n === 'string' && typeof o.c === 'string'
}

/** Запечатать ключ звонка для собеседника. */
export async function sealCallKey(key: string, binding: CallKeyBinding, peerPublicKey: string): Promise<CallKeyEnvelope> {
  const id = await getOrCreateIdentity()
  const nonce = nacl.randomBytes(nacl.box.nonceLength)
  // Порядок полей фиксирован — так же собирает открытый текст iOS.
  const plain = JSON.stringify({
    v: 1,
    k: key,
    chat_id: binding.chatId,
    caller_id: binding.callerId,
    callee_id: binding.calleeId,
  })
  const cipher = nacl.box(decodeUTF8(plain), nonce, decodeBase64(peerPublicKey), id.secretKey)

  return {
    v: 1,
    alg: 'box',
    spk: encodeBase64(id.publicKey),
    rpk: peerPublicKey,
    n: encodeBase64(nonce),
    c: encodeBase64(cipher),
  }
}

/** Конверт адресован этому устройству (как получателю или как отправителю). */
export async function envelopeBelongsToThisDevice(env: CallKeyEnvelope): Promise<boolean> {
  const id = await getIdentity()
  if (!id) return false
  const mine = encodeBase64(id.publicKey)
  return env.rpk === mine || env.spk === mine
}

/**
 * Открыть конверт своим ключом устройства и проверить привязку к звонку.
 * Работает и у собеседника (конверт адресован ему), и у звонящего после
 * перезагрузки (конверт создан этим устройством).
 */
export async function openCallKey(env: unknown, expected: CallKeyBinding): Promise<OpenCallKeyResult> {
  if (!isCallKeyEnvelope(env)) return { ok: false, reason: 'bad_envelope' }

  const id = await getIdentity()
  if (!id) return { ok: false, reason: 'no_identity' }

  const mine = encodeBase64(id.publicKey)
  let peer: string
  if (env.rpk === mine) peer = env.spk
  else if (env.spk === mine) peer = env.rpk
  else return { ok: false, reason: 'other_device' }

  let opened: Uint8Array | null
  try {
    opened = nacl.box.open(decodeBase64(env.c), decodeBase64(env.n), decodeBase64(peer), id.secretKey)
  } catch {
    opened = null
  }
  if (!opened) return { ok: false, reason: 'bad_envelope' }

  let plain: any
  try {
    plain = JSON.parse(encodeUTF8(opened))
  } catch {
    return { ok: false, reason: 'bad_envelope' }
  }

  let keyOk: boolean
  try {
    keyOk = typeof plain?.k === 'string' && decodeBase64(plain.k).length === 32
  } catch {
    keyOk = false
  }
  if (plain?.v !== 1 || !keyOk) return { ok: false, reason: 'bad_envelope' }

  // Привязка: конверт от другого звонка сервер подставить не сможет.
  if (Number(plain.chat_id) !== Number(expected.chatId)
    || Number(plain.caller_id) !== Number(expected.callerId)
    || Number(plain.callee_id) !== Number(expected.calleeId)) {
    return { ok: false, reason: 'mismatch' }
  }

  return { ok: true, key: plain.k, peerPublicKey: peer }
}
